import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { onINP, onLCP } from 'web-vitals/attribution';

const SESSION_KEY = 'perf_session_id';
const DOC_KEY = 'perf_metrics_doc_id';

class MetricsService {
	constructor() {
		this.sessionId = null;
		this.metricsDocId = null;
		this.metricsQueue = [];
		this.sendInterval = null;
		this.isInitialized = false;
		this.activeMarks = new Set();
		this.isSyncing = false;
		this.vitalsInitialized = false;
		this.userInfo = null;
		this.deviceInfo = null;
		this._enabled = true;
		this._watcherInitialized = false;
	}

	/**
	 * Evaluates whether metrics collection is currently enabled.
	 * Defaults to true before initStoreWatcher has been called.
	 */
	_isEnabled() {
		return this._enabled;
	}

	/**
	 * Registers a Vuex store watcher on the frontendMetricsEnabled flag.
	 * The watcher reactively handles enabled/disabled transitions:
	 * - enabled → disabled: pauses collection, clears queue and interval
	 * - disabled → enabled: re-initializes the session and resumes collection
	 *
	 * Must be called once during login (from the auth store action).
	 * Guarded against double-registration by _watcherInitialized flag.
	 *
	 * @param {Object} store - The Vuex store instance (this inside actions)
	 */
	initStoreWatcher(store) {
		if (this._watcherInitialized) return;
		this._watcherInitialized = true;

		const getFlag = (state) => state.setup?.setup?.frontendMetricsEnabled;

		// Set initial value (defaults to true if undefined)
		this._enabled = getFlag(store.state) !== false;

		store.watch(getFlag, (newValue, oldValue) => {
			const enabled = newValue !== false;
			const wasEnabled = oldValue !== false;
			this._enabled = enabled;

			if (enabled && !wasEnabled) {
				if (this.userInfo && this.deviceInfo) {
					this.initSession(this.userInfo, this.deviceInfo);
				}
			} else if (!enabled && wasEnabled) {
				this.pause();
			}
		});
	}

	/**
	 * Standardized listeners for Web Vitals.
	 * These pipe directly into the metrics queue.
	 */
	initWebVitals() {
		if (this.vitalsInitialized) return;

		onLCP((metric) => {
			this.addMetric('LCP', metric.value);
		});

		onINP(
			(metric) => {
				const interactionTarget = metric.attribution?.interactionTarget;
				let selector = 'unknown';

				if (metric.value <= 200) {
					return;
				}

				if (interactionTarget) {
					const parts = interactionTarget.split('>');
					const lastPart = parts[parts.length - 1].trim();
					selector = lastPart;
				}

				this.addMetric(`INP_${selector}`, metric.value);
			},
			{ reportAllChanges: true }
		);

		this.vitalsInitialized = true;
		console.log('%c[Metrics] Web Vitals (INP/LCP) Active', 'color: blue; font-weight: bold');
	}

	/**
	 * Initializes the metrics session. Checks localStorage to avoid duplicate
	 * DB entries on browser refreshes. Respects the frontendMetricsEnabled flag
	 * from setup — if disabled, the session is not created.
	 *
	 * @param {Object} userInfo - User identification { username, ... }
	 * @param {Object} deviceInfo - Device metadata { deviceMemory, connectionType, ... }
	 * @returns {Promise<string|null>} The sessionId or null if disabled/failed
	 */
	async initSession(userInfo, deviceInfo) {
		this.userInfo = userInfo;
		this.deviceInfo = deviceInfo;

		if (!this._isEnabled()) {
			return null;
		}

		const savedSessionId = localStorage.getItem(SESSION_KEY);
		const savedDocId = localStorage.getItem(DOC_KEY);

		if (savedSessionId && savedDocId) {
			this.sessionId = savedSessionId;
			this.metricsDocId = savedDocId;
			this.isInitialized = true;
			this.startPeriodicSend(10000);
			this.initWebVitals();
			return this.sessionId;
		}

		return await this.createNewSession();
	}

	/**
	 * Creates a new metrics session by posting to the backend.
	 * Guarded by the frontendMetricsEnabled flag.
	 *
	 * @returns {Promise<string|null>} The sessionId or null if disabled/failed
	 */
	async createNewSession() {
		if (!this._isEnabled()) {
			return null;
		}

		try {
			this.sessionId = uuidv4();
			const payload = {
				sessionId: this.sessionId,
				userName: this.userInfo.username,
				application: 'web',
				deviceInfo: this.deviceInfo,
				metrics: [],
			};

			const response = await axios.post('/api/performance-metrics', payload);
			this.metricsDocId = response.data._id || response.data.id;

			localStorage.setItem(SESSION_KEY, this.sessionId);
			localStorage.setItem(DOC_KEY, this.metricsDocId);

			this.isInitialized = true;
			this.startPeriodicSend(10000);
			this.initWebVitals();
			return this.sessionId;
		} catch (error) {
			console.error('[Metrics] Failed to create session:', error);
			return null;
		}
	}

	/**
	 * Pauses metrics collection. Clears the periodic send interval,
	 * discards queued metrics, and marks the service as uninitialized.
	 */
	pause() {
		if (this.sendInterval) clearInterval(this.sendInterval);
		this.sendInterval = null;
		this.metricsQueue = [];
		this.isInitialized = false;
	}

	/**
	 * Adds a metric to the local queue for batched sending.
	 * Ignored when the service is not initialized or metrics are disabled.
	 *
	 * @param {string} type - Metric identifier (e.g. "LCP", "INP_button#save", "Planning-load")
	 * @param {number} duration - Measured value in milliseconds
	 */
	addMetric(type, duration) {
		if (!this.isInitialized || !this._isEnabled()) return;

		const metricEntry = {
			type: type,
			duration: Math.round(duration * 100) / 100,
			timestamp: new Date().toISOString(),
		};

		this.metricsQueue.push(metricEntry);
		console.log(`[Queue] Added ${type}`, metricEntry);
	}

	/**
	 * Periodic "Flush" — sends the queued metrics batch to the backend via PATCH.
	 * Handles 400 responses by re-initializing the session (e.g. when the server
	 * deleted the session via retention cron).
	 */
	async flushQueue() {
		if (!this.isInitialized || this.isSyncing || this.metricsQueue.length === 0 || !this._isEnabled()) return;

		this.isSyncing = true;
		const batchToSend = [...this.metricsQueue];
		this.metricsQueue = [];

		try {
			await axios.patch(`/api/performance-metrics/${this.metricsDocId}`, {
				metrics: batchToSend,
			});
		} catch (error) {
			if (error.response && error.response.status === 400) {
				console.warn('[Metrics] Session not found (deleted by server). Re-initializing...');

				this.handleSessionStale();
				this.metricsQueue = [...batchToSend, ...this.metricsQueue];
				await this.createNewSession();
			} else {
				console.error('[Sync] Failed to send metrics batch:', error);
				this.metricsQueue = [...batchToSend, ...this.metricsQueue];
			}
		} finally {
			this.isSyncing = false;
		}
	}

	/**
	 * Resets the service state when the backend record is missing.
	 * Clears session IDs from both memory and localStorage.
	 */
	handleSessionStale() {
		this.isInitialized = false;
		this.sessionId = null;
		this.metricsDocId = null;
		localStorage.removeItem(SESSION_KEY);
		localStorage.removeItem(DOC_KEY);
	}

	/**
	 * Starts a performance mark for custom timing measurement.
	 * No-op when metrics collection is disabled.
	 *
	 * @param {string} markName - Unique identifier for this measurement
	 * @returns {string} The markName (for chaining with endTracking)
	 */
	startTracking(markName) {
		if (!this._isEnabled()) return markName;

		const startTime = performance.now();
		const clockTime = new Date().toLocaleTimeString();

		performance.clearMarks(markName);
		performance.mark(markName);
		this.activeMarks.add(markName);

		console.log(`%c[START] ${markName}`, 'color: green; font-weight: bold', {
			at: clockTime,
			perfNow: startTime.toFixed(2),
		});

		return markName;
	}

	/**
	 * Ends a performance mark, computes the duration, and queues the metric
	 * if above the reporting threshold (100ms). Cleans up marks and measures.
	 * No-op when metrics collection is disabled.
	 *
	 * @param {string} markName - The markName previously passed to startTracking
	 * @param {string} componentName - Label for the metric in the queue
	 */
	endTracking(markName, componentName) {
		if (!this._isEnabled()) {
			return;
		}

		const endTime = performance.now();
		const clockTime = new Date().toLocaleTimeString();
		const endMark = `${markName}-end`;
		const measureName = `${markName}-measure`;

		try {
			performance.mark(endMark);
			performance.measure(measureName, markName, endMark);

			const measure = performance.getEntriesByName(measureName)[0];

			const thresholdForReporting = 100;
			if (measure.duration < thresholdForReporting) {
				console.log(
					`%c[SKIPPED] ${markName}`,
					'color: gray',
					`Duration: ${measure.duration.toFixed(2)}ms (under ${thresholdForReporting}ms threshold)`
				);
				return;
			}

			console.log(`%c[END] ${markName}`, 'color: red; font-weight: bold', {
				at: clockTime,
				perfNow: endTime.toFixed(2),
				calculatedDuration: measure ? measure.duration.toFixed(2) + 'ms' : 'N/A',
			});

			if (measure) {
				this.addMetric(componentName, measure.duration);
			}
		} catch (error) {
			console.warn(`[Metrics] Measurement failed: ${markName}`, error);
		} finally {
			performance.clearMarks(markName);
			performance.clearMarks(endMark);
			performance.clearMeasures(measureName);
			this.activeMarks.delete(markName);
		}
	}

	startPeriodicSend(intervalMs) {
		if (this.sendInterval) clearInterval(this.sendInterval);
		this.sendInterval = setInterval(() => this.flushQueue(), intervalMs);
	}

	/**
	 * Final flush and cleanup. Called on logout to ensure any remaining
	 * queued metrics are sent to the backend before the session ends.
	 */
	async endSession() {
		if (this.sendInterval) clearInterval(this.sendInterval);

		if (this._isEnabled()) {
			await this.flushQueue();
		}

		this.handleSessionStale();
		console.log('[Metrics] Session ended and queue flushed.');
	}
}

export default new MetricsService();
